import { useMemo, useState } from 'react';

import Input from '@cartwhl/ui/atoms/Input';
import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';

import { useStore } from '../batchGenerationPromptStore';

export default function MotionPromptSection() {
  const store = useStore();
  const { promptRows } = store.state;
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const autoResizeOptions = useMemo(() => ({ minHeight: 32 }), []);

  const isEmpty = promptRows.length === 0;

  if (isEmpty) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between">
          <Text variant="studio/body/medium/sm" className="text-primary">
            Add your motions
          </Text>
          <Text
            variant="studio/body/medium/sm"
            className="text-primary text-blue cursor-pointer"
            onClick={() => store.addMotionRow()}
          >
            + Add a motion
          </Text>
        </div>
        <div className="bg-secondary border-primary flex min-h-[200px] flex-col items-center justify-center rounded-lg border p-8"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row justify-between">
        <Text variant="studio/body/medium/sm" className="text-primary">
          Add your motions{' '}
          <span className="text-gray-60">({promptRows.length})</span>
        </Text>
        <Text
          variant="studio/body/medium/sm"
          className="text-primary text-blue cursor-pointer"
          onClick={() => store.addMotionRow()}
        >
          + Add another motion
        </Text>
      </div>
      <div className="bg-secondary flex flex-col gap-4 rounded-lg p-4">
        {promptRows.map((row, index) => {
          const promptError = row.prompt.trim().length === 0;
          const variationError =
            row.variationCount == null ||
            row.variationCount < 1 ||
            row.variationCount > 100;
          const hasError = promptError || variationError;
          const isHovered = hoveredRowId === row.id;
          const canRemove = promptRows.length > 1;
          const showRemove = canRemove && isHovered;

          return (
            <div
              key={row.id}
              className="flex items-stretch gap-0"
              onMouseEnter={() => setHoveredRowId(row.id)}
              onMouseLeave={() => setHoveredRowId(null)}
            >
              {/* Number in gray (left) */}
              <div className="flex w-10 shrink-0 items-start pt-4">
                <Text
                  variant="studio/body/medium/sm"
                  className="text-secondary font-medium"
                >
                  {index + 1}.
                </Text>
              </div>

              {/* White card with 3 sections and vertical dividers */}
              <div className="border-primary bg-primary flex min-w-0 flex-1 items-stretch rounded-lg border">
                <div className="border-primary flex min-w-0 flex-1 flex-col gap-2.5 border-r px-4 py-4">
                  <Text
                    variant="studio/body/medium/sm"
                    className={`shrink-0 ${promptError ? 'text-red' : 'text-secondary'}`}
                  >
                    Your motion prompt
                  </Text>
                  <Input
                    as="textarea"
                    name={`prompt-${row.id}`}
                    placeholder="Ex. Karate punch and dodge"
                    value={row.prompt}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      store.updateMotionRow(row.id, 'prompt', e.target.value)
                    }
                    autoResize
                    autoResizeOptions={autoResizeOptions}
                    className="w-full resize-none bg-transparent transition-none hover:bg-transparent focus-visible:bg-transparent dark:bg-transparent dark:hover:bg-transparent dark:focus-visible:bg-transparent"
                  />
                </div>

                <div className="border-primary flex shrink-0 flex-col gap-2.5 px-4 py-4">
                  <Text
                    variant="studio/body/medium/sm"
                    className={`shrink-0 ${variationError ? 'text-red' : 'text-secondary'}`}
                  >
                    Variations
                  </Text>
                  <Input
                    required={false}
                    type="number"
                    placeholder="Ex. 25"
                    min={1}
                    max={100}
                    name={`variation-${row.id}`}
                    value={row.variationCount || ''}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      store.updateMotionRow(
                        row.id,
                        'variationCount',
                        Number.isNaN(value) ? 0 : Math.max(1, value),
                      );
                    }}
                    className="w-32 bg-transparent hover:bg-transparent focus-visible:bg-transparent dark:bg-transparent dark:hover:bg-transparent dark:focus-visible:bg-transparent"
                  />
                </div>
              </div>

              {/* Checkmark / X on gray (right) */}
              <div className="flex w-12 shrink-0 items-start justify-center pt-4">
                {showRemove ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      store.removeMotionRow(row.id);
                    }}
                    className="text-secondary hover:text-primary focus:text-primary focus:ring-blue flex h-8 w-8 cursor-pointer items-center justify-center rounded transition-colors focus:ring-2 focus:outline-none"
                    aria-label="Remove motion"
                  >
                    <CrossIcon className="h-5 w-5" />
                  </button>
                ) : hasError ? (
                  <span
                    title={
                      promptError && variationError
                        ? 'Prompt and variations required'
                        : promptError
                          ? 'Prompt required'
                          : 'Valid variation (1–100) required'
                    }
                  >
                    <WarningIcon className="text-red h-5 w-5" />
                  </span>
                ) : (
                  <span title="Valid">
                    <CheckmarkIcon className="text-green h-5 w-5 shrink-0" />
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
